import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { WizardStep } from './types';

/**
 * WizardFooter Component
 *
 * Footer navigation for AI Briefing Wizard with dynamic button states.
 * Handles Cancel, Back, Next, Generate, Tweak, and Add to Caption actions.
 */

interface WizardFooterProps {
  currentStep: WizardStep;
  canGoBack: boolean;
  canGoNext: boolean;
  isLastFormStep: boolean;  // True on last step before generating
  isGenerating: boolean;
  showResult: boolean;  // True when showing result
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
  onGenerate: () => void;
  onAddToCaption: () => void;
  onTweak: () => void;
}

export const WizardFooter = ({
  currentStep: _currentStep, // Kept for potential future use (e.g., step-specific UI)
  canGoBack,
  canGoNext,
  isLastFormStep,
  isGenerating,
  showResult,
  onBack,
  onNext,
  onCancel,
  onGenerate,
  onAddToCaption,
  onTweak,
}: WizardFooterProps) => {
  const { t } = useTranslation('ai');

  return (
    <div className="flex-shrink-0 flex items-center justify-between border-t pt-4 mt-4 bg-white">
      {/* Left side: Cancel button (always visible) */}
      <div>
        <Button variant="outline" onClick={onCancel}>
          {t('ai_wizard_cancel')}
        </Button>
      </div>

      {/* Right side: Conditional navigation buttons */}
      <div className="flex items-center gap-3">
        {/* Back button - only if canGoBack and not generating/result */}
        {canGoBack && !isGenerating && !showResult && (
          <Button variant="outline" onClick={onBack}>
            {t('ai_wizard_back')}
          </Button>
        )}

        {/* Next/Generate button - during form steps */}
        {!isGenerating && !showResult && (
          <Button
            onClick={isLastFormStep ? onGenerate : onNext}
            disabled={!canGoNext}
          >
            {isLastFormStep ? t('ai_wizard_generate') : t('ai_wizard_next')}
          </Button>
        )}

        {/* Result Step Actions */}
        {showResult && (
          <>
            <Button variant="outline" onClick={onTweak}>
              {t('ai_wizard_start_over', { defaultValue: 'Start Over' })}
            </Button>
            <Button onClick={onAddToCaption}>
              {t('ai_wizard_use_caption', { defaultValue: 'Use Caption' })}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
